import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { environment } from '@/environments/environment';
import { AuthSession, clearAuthSession, persistAuthSession, readAuthSession, readRememberedUsername } from './auth-session';

interface TokenResponse {
    access_token?: string;
    expires_in?: number;
    token_type?: string;
}

@Injectable({
    providedIn: 'root'
})
/* v8 ignore start */
export class AuthService {
/* v8 ignore stop */
    private readonly platformId = inject(PLATFORM_ID);
    private readonly http = inject(HttpClient);
    private readonly sessionState = signal<AuthSession | null>(this.restore());

    readonly session = this.sessionState.asReadonly();

    login(username: string, password: string, rememberMe: boolean): Observable<void> {
        const body = new URLSearchParams({
            username,
            password,
            remember_me: String(rememberMe)
        });
        return this.requestToken('/auth/token', body, rememberMe, username);
    }

    loginWithGoogle(authorizationCode: string, rememberMe: boolean): Observable<void> {
        const body = new URLSearchParams({
            code: authorizationCode,
            remember_me: String(rememberMe)
        });
        return this.requestToken('/auth/social/token?provider=google', body, rememberMe, null);
    }

    googleClientId(): Observable<string | null> {
        return this.http.get<{ clientId?: string }>(`${environment.apiUrl}/auth/social/config?provider=google`).pipe(
            map((response) => {
                const clientId = response.clientId?.trim();
                return clientId ? clientId : null;
            }),
            catchError(() => of(null))
        );
    }

    logout(): void {
        this.sessionState.set(null);
        if (isPlatformBrowser(this.platformId)) {
            clearAuthSession();
        }
    }

    getAccessToken(): string | null {
        const current = this.sessionState();
        if (!current) {
            return null;
        }
        if (current.expiresAt <= Date.now()) {
            this.logout();
            return null;
        }
        return current.accessToken;
    }

    isAuthenticated(): boolean {
        return this.getAccessToken() != null;
    }

    rememberedUsername(): string | null {
        if (!isPlatformBrowser(this.platformId)) {
            return null;
        }
        return readRememberedUsername();
    }

    private restore(): AuthSession | null {
        if (!isPlatformBrowser(this.platformId)) {
            return null;
        }
        return readAuthSession();
    }

    private requestToken(
        path: string,
        body: URLSearchParams,
        rememberMe: boolean,
        username: string | null
    ): Observable<void> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded'
        });

        return this.http.post<TokenResponse>(`${environment.apiUrl}${path}`, body.toString(), { headers }).pipe(
            map((response) => {
                if (!response.access_token) {
                    throw new Error('Resposta de autenticação sem access_token.');
                }
                return response;
            }),
            tap((response) => {
                const accessToken = response.access_token as string;
                const session: AuthSession = {
                    accessToken,
                    expiresAt: this.resolveExpiresAt(accessToken, response.expires_in),
                    rememberMe
                };
                this.sessionState.set(session);
                if (isPlatformBrowser(this.platformId)) {
                    persistAuthSession(session, username ?? decodeJwtSubject(accessToken) ?? '');
                }
            }),
            map(() => undefined)
        );
    }

    private resolveExpiresAt(accessToken: string, expiresIn: number | undefined): number {
        if (typeof expiresIn === 'number' && Number.isFinite(expiresIn) && expiresIn > 0) {
            return Date.now() + expiresIn * 1000;
        }
        return decodeJwtExpiration(accessToken) ?? Date.now();
    }
}

function decodeJwtExpiration(accessToken: string): number | null {
    const exp = decodeJwtPayload(accessToken)?.['exp'];
    return typeof exp === 'number' ? exp * 1000 : null;
}

function decodeJwtSubject(accessToken: string): string | null {
    const subject = decodeJwtPayload(accessToken)?.['sub'];
    return typeof subject === 'string' && subject.trim().length > 0 ? subject : null;
}

function decodeJwtPayload(accessToken: string): Record<string, unknown> | null {
    const payload = accessToken.split('.')[1];
    if (!payload) {
        return null;
    }
    try {
        const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
        const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
        return JSON.parse(atob(padded)) as Record<string, unknown>;
    } catch {
        return null;
    }
}
